import React, { useState } from "react";
import { toast } from "sonner";
import { errMsg, fmt, useApi, useLoad, useTaxonomy } from "../../lib/api";
import { Empty, ErrorBox, Field, Loading, StatusBadge } from "../../components/kit";
import { FilterBar, OrgForm, OrgMembers, qs } from "./OpsOrgAdmin";

function useAction(reload: () => void) {
  const api = useApi();
  return async (method: string, path: string, body: any, ok: string) => {
    try { await api(method, path, body); toast.success(ok); reload(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
}

function SubEditor({ o, act }: { o: any; act: any }) {
  const [tier, setTier] = useState(o.subscription?.tier || "free");
  const [status, setStatus] = useState(o.subscription?.status || "test_active");
  return (
    <div className="flex flex-wrap items-center gap-1">
      <select aria-label={`Tier for ${o.name}`} className="input !min-h-[32px] !w-auto !py-0 text-xs" value={tier} onChange={(e) => setTier(e.target.value)} data-testid={`ops-sub-tier-${o.id}`}>
        <option value="free">free</option><option value="basic">basic</option><option value="pro">pro</option>
      </select>
      <select aria-label={`Status for ${o.name}`} className="input !min-h-[32px] !w-auto !py-0 text-xs" value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`ops-sub-status-${o.id}`}>
        <option value="test_active">test active</option><option value="test_paused">test paused</option><option value="test_cancelled">test cancelled</option>
      </select>
      <button className="btn btn-sm btn-ghost" onClick={() => act("put", `/ops/subscriptions/${o.id}`, { tier, status, notes: "" }, "Test subscription updated")} data-testid={`ops-sub-save-${o.id}`}>Save</button>
    </div>
  );
}

export function OpsOrgs() {
  const { categories } = useTaxonomy();
  const [f, setF] = useState({ q: "", status: "", category_id: "" });
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { data, loading, error, reload } = useLoad<any[]>(`/ops/orgs?${qs(f)}`);
  const act = useAction(reload);
  const toolbar = (
    <FilterBar>
      <input aria-label="Search organizations" placeholder="Search name…" className="input !w-auto flex-1" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="ops-orgs-search-input" />
      <select aria-label="Status filter" className="input !w-auto" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="ops-orgs-status-filter">
        <option value="">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="suspended">Suspended</option>
      </select>
      <select aria-label="Trade filter" className="input !w-auto" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="ops-orgs-category-filter">
        <option value="">All trades</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <button className="btn btn-primary" onClick={() => setCreating(true)} data-testid="ops-org-new-button">New vendor org</button>
    </FilterBar>
  );
  if (error) return <>{toolbar}<ErrorBox message={error} onRetry={reload} /></>;
  return (
    <div>
      {toolbar}
      {creating && <div className="mb-4"><OrgForm onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} /></div>}
      {loading && !data && <Loading />}
      {data?.length === 0 && <Empty title="No organizations match" />}
    <div className="card overflow-x-auto">
      <p className="mb-2 text-xs text-[var(--muted)]"><span className="badge badge-amber">Test subscriptions only</span> No payments are processed. Tier never affects organic matching.</p>
      <table className="table" data-testid="ops-orgs-table">
        <thead><tr><th>Organization</th><th>Status</th><th>Availability</th><th>Exposures</th><th>Test plan</th><th>Actions</th></tr></thead>
        <tbody>
          {data?.map((o) => (
            <React.Fragment key={o.id}>
            <tr data-testid={`ops-org-row-${o.id}`}>
              <td><strong>{o.name}</strong><div className="mono text-[10px] text-[var(--muted)]">{o.id}</div></td>
              <td><StatusBadge value={o.status} testId={`ops-org-status-${o.id}`} /></td>
              <td><StatusBadge value={o.profile?.availability} /></td>
              <td className="mono">{o.organic_exposures || 0}</td>
              <td><SubEditor o={o} act={act} /></td>
              <td className="space-x-1 whitespace-nowrap">
                {o.status !== "approved" && <button className="btn btn-sm btn-primary" onClick={() => act("post", `/ops/orgs/${o.id}/status`, { status: "approved" }, "Organization approved")} data-testid={`ops-org-approve-${o.id}`}>Approve</button>}
                {o.status !== "suspended" && <button className="btn btn-sm btn-danger" onClick={() => act("post", `/ops/orgs/${o.id}/status`, { status: "suspended" }, "Organization suspended")} data-testid={`ops-org-suspend-${o.id}`}>Suspend</button>}
                <button className="btn btn-sm btn-ghost" onClick={() => setOpen(open === o.id ? null : o.id)} aria-expanded={open === o.id} data-testid={`ops-org-manage-${o.id}`}>{open === o.id ? "Close" : "Edit / members"}</button>
              </td>
            </tr>
            {open === o.id && (
              <tr><td colSpan={6} className="space-y-3 bg-[var(--paper)]">
                <OrgForm org={o} onCancel={() => setOpen(null)} onSaved={reload} />
                <h4 className="font-bold">Members</h4>
                <OrgMembers orgId={o.id} />
              </td></tr>
            )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

export function OpsInvites() {
  const { data, loading, error, reload } = useLoad<any[]>("/ops/invitations");
  const orgs = useLoad<any[]>("/ops/orgs");
  const act = useAction(reload);
  const [v, setV] = useState({ email: "", kind: "customer", org_name: "", org_id: "", org_role: "owner" });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: any = { email: v.email, kind: v.kind };
    if (v.kind === "vendor") Object.assign(body, v.org_id ? { org_id: v.org_id, org_role: v.org_role } : { org_name: v.org_name });
    if (await act("post", "/ops/invitations", body, "Invitation recorded — no email sent")) setV({ ...v, email: "", org_name: "" });
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
      <form onSubmit={submit} className="card space-y-4" data-testid="ops-invite-form">
        <h3 className="text-2xl font-bold">Invite after review</h3>
        <p className="text-xs text-[var(--muted)]">No email is sent. After recording, share the sign-up link <span className="mono">{window.location.origin}/sign-up</span> with the person yourself. Access applies when they sign up with this exact, verified email.</p>
        <Field label="Email" id="inv-email"><input id="inv-email" type="email" required className="input" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} data-testid="ops-invite-email-input" /></Field>
        <Field label="Account type" id="inv-kind">
          <select id="inv-kind" className="input" value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value })} data-testid="ops-invite-kind-select">
            <option value="customer">Customer</option><option value="vendor">Vendor organization member</option>
          </select>
        </Field>
        {v.kind === "vendor" && (
          <>
            <Field label="Existing organization" id="inv-org" hint="Leave empty to create a new pending organization.">
              <select id="inv-org" className="input" value={v.org_id} onChange={(e) => setV({ ...v, org_id: e.target.value })} data-testid="ops-invite-org-select">
                <option value="">— New organization —</option>{orgs.data?.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </Field>
            {v.org_id ? (
              <Field label="Role" id="inv-role">
                <select id="inv-role" className="input" value={v.org_role} onChange={(e) => setV({ ...v, org_role: e.target.value })} data-testid="ops-invite-role-select"><option value="owner">Owner</option><option value="staff">Staff</option></select>
              </Field>
            ) : (
              <Field label="New organization name" id="inv-orgname"><input id="inv-orgname" required className="input" value={v.org_name} onChange={(e) => setV({ ...v, org_name: e.target.value })} data-testid="ops-invite-orgname-input" /></Field>
            )}
          </>
        )}
        <button className="btn btn-primary" data-testid="ops-invite-submit-button">Record invitation</button>
      </form>
      <div>
        {loading && !data && <Loading />}
        {error && <ErrorBox message={error} onRetry={reload} />}
        {data?.length === 0 && <Empty title="No invitations yet" />}
        <ul className="space-y-2" data-testid="ops-invite-list">
          {data?.map((i) => (
            <li key={i.id} className="card flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`ops-invite-${i.id}`}>
              <div><strong>{i.email}</strong> <span className="text-[var(--muted)]">· {i.kind}{i.org_role ? ` (${i.org_role})` : ""} · {fmt(i.created_at)}</span></div>
              <div className="flex items-center gap-2"><StatusBadge value={i.status} />{i.status === "pending" && <button className="btn btn-sm btn-danger" onClick={() => act("post", `/ops/invitations/${i.id}/revoke`, {}, "Invitation revoked")} data-testid={`ops-invite-revoke-${i.id}`}>Revoke</button>}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function OpsAds() {
  const { data, loading, error, reload } = useLoad<any[]>("/ops/ads");
  const act = useAction(reload);
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data?.length) return <Empty title="No ads submitted" />;
  return (
    <ul className="grid gap-3 md:grid-cols-2" data-testid="ops-ads-list">
      {data.map((a) => (
        <li key={a.id} className="sponsored-zone text-sm" data-testid={`ops-ad-${a.id}`}>
          <div className="flex justify-between gap-2"><strong>{a.title}</strong><StatusBadge value={a.status} testId={`ops-ad-status-${a.id}`} /></div>
          <p className="text-[var(--muted)]">{a.org_name} — {a.body}</p>
          <div className="mt-2 flex gap-2">
            {a.status !== "approved" && <button className="btn btn-sm btn-primary" onClick={() => act("post", `/ops/ads/${a.id}/review`, { decision: "approved" }, "Ad approved")} data-testid={`ops-ad-approve-${a.id}`}>Approve</button>}
            {a.status !== "rejected" && <button className="btn btn-sm btn-danger" onClick={() => act("post", `/ops/ads/${a.id}/review`, { decision: "rejected" }, "Ad rejected")} data-testid={`ops-ad-reject-${a.id}`}>Reject</button>}
          </div>
        </li>
      ))}
    </ul>
  );
}
